import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import { Card, Button, Alert, Row, Col, Spinner } from "reactstrap";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
} from "../../components/Component";
import {
  Form,
  Select,
  Table,
  InputNumber,
  DatePicker,
  Input,
  Checkbox,
  TableColumnsType,
  Space,
  Tag,
  Empty,
  Alert as AntAlert,
  Descriptions,
  Divider,
  Modal,
  Upload,
  Button as AntButton,
  Flex,
  Spin,
  message,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  CalculatorOutlined,
  SaveOutlined,
  FileTextOutlined,
  UploadOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import RABillApis from "../../api/WorkOrderManagement/RABillApis";
import paymentApis from "../../api/WorkOrderManagement/PaymentsApis";
import TransactionFlowDefaultTemplate from "../others/transactionFlowDefaultTemplate";
import { Link } from "react-router-dom";
import {
  PaymentData,
  PaymentType,
  PaymentMethod,
  PaymentStatus,
  UserRole,
  ApprovedRaBillData,
  PaymentAllocation,
  PaymentDeduction,
} from "./types";
import {
  mockApprovedRaBillsData,
  getEligibleRaBillsForPayment,
  mockContractorOptions,
} from "./mockData/approvedRaBillsData";
import {
  mockLegalEntityOptions,
  mockBankAccountOptions,
  mockTdsSectionOptions,
  mockPaymentMethodOptions,
} from "./mockData/paymentData";
import dropdownApis from "../../api/WorkOrderManagement/DropdownApis";

const { Option } = Select;
const { TextArea } = Input;

interface FormData {
  paymentVoucherNumber: string;
  paymentDate: dayjs.Dayjs | null;
  paymentType: PaymentType;
  contractorId: string;
  legalEntityId: string;
  paymentMethod: PaymentMethod;
  bankAccountId: string;
  tdsSection: string;
  tdsRate: number;
  narration: string;
  allocations: PaymentAllocation[];
}

const CreatePayment: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const duplicateId = searchParams.get("duplicate");
  const isEdit = Boolean(editId);
  const isDuplicate = Boolean(duplicateId);

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedRaBills, setSelectedRaBills] = useState<ApprovedRaBillData[]>(
    []
  );
  const [tempSelectedRaBills, setTempSelectedRaBills] = useState<
    ApprovedRaBillData[]
  >([]);
  const [eligibleRaBills, setEligibleRaBills] = useState<any[]>([]);
  const [showEligibleBills, setShowEligibleBills] = useState(false);
  const [selectedContractor, setSelectedContractor] = useState<string>("");
  const [raBillsLoading, setRaBillsLoading] = useState(false);
  const [raBillsPagination, setRaBillsPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [screenSize, setScreenSize] = useState(window.innerWidth);
  const [fileList, setFileList] = useState<any[]>([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [contractorOptions, setContractorOptions] = useState<any[]>([]);
  const [contractorLoading, setContractorLoading] = useState(false);

  // Commercial calculations
  const [sumOfAllocations, setSumOfAllocations] = useState(0);
  const [tdsThisPayment, setTdsThisPayment] = useState(0);
  const [otherAdjustments, setOtherAdjustments] = useState(0);
  const [netPayable, setNetPayable] = useState(0);

  useEffect(() => {
    loadEligibleRaBills();
    initializeForm();
    loadContractorOptions();
  }, []);

  useEffect(() => {
    const handleResize = () => setScreenSize(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const loadEligibleRaBills = async (params?: any) => {
    setRaBillsLoading(true);
    try {
      const {
        current = 1,
        sortOrder = "DESC",
        pageSize = 10,
        direction = "P",
        pageDiff = 0,
        paginationId = null,
        searchQuery = null,
        selectFilter1 = null,
        startDate = null,
        endDate = null,
        contractorIds = null,
      } = params || {};

      const apiResponse: any = await RABillApis.listRABill({
        sortOrder,
        pageSize,
        direction,
        pageDiff,
        paginationId,
        searchQuery,
        status: selectFilter1,
        startDate,
        endDate,
        filterListType: "OutStanding",
        contractorIds,
      });

      const { data, status } = apiResponse;
      if (status) {
        const { list, count } = data || {};
        setEligibleRaBills(list || []);
        setRaBillsPagination((prev) => ({
          ...prev,
          total: Number(count) || 0,
          current: Number(current),
          pageSize: Number(pageSize),
        }));
      }
    } catch (error) {
      console.error("Error loading eligible RA bills:", error);
      // Fallback to mock data if API fails
      const eligible = getEligibleRaBillsForPayment();
      setEligibleRaBills(eligible);
      setRaBillsPagination((prev) => ({
        ...prev,
        total: eligible.length,
      }));
    } finally {
      setRaBillsLoading(false);
    }
  };

  const initializeForm = () => {
    // Set default values
    form.setFieldsValue({
      paymentVoucherNumber: generatePaymentVoucherNumber(),
      paymentDate: dayjs(),
      paymentType: PaymentType.RA,
      tdsRate: 1.0,
    });
  };

  const generatePaymentVoucherNumber = () => {
    const currentDate = dayjs();
    const year = currentDate.year();
    const month = currentDate.format("MM");
    const randomNum = Math.floor(Math.random() * 9999)
      .toString()
      .padStart(4, "0");
    return `PAY/${year}/HYD/${randomNum}`;
  };

  const loadContractorOptions = async () => {
    setContractorLoading(true);
    try {
      const response = await dropdownApis.fetchContractorDropDown();
      if (response.status && response.data) {
        const options = response.data.map((contractor: any) => ({
          value: contractor.contractorID.toString(),
          label: contractor.companyName,
          contractorOrgId: contractor.contractorOrgId,
          contactPerson: contractor.contactPerson,
        }));
        setContractorOptions(options);
      }
    } catch (error) {
      console.error("Error loading contractor options:", error);
      // message.error("Error loading contractor options");
      // Fallback to mock data if API fails
      // setContractorOptions(mockContractorOptions);
    } finally {
      setContractorLoading(false);
    }
  };

  const handleContractorChange = async (contractorId: string) => {
    setSelectedContractor(contractorId);

    // Clear existing selections if contractor changes
    setSelectedRaBills([]);
    setTempSelectedRaBills([]);
    form.setFieldsValue({ allocations: [] });
    calculateTotals([]);

    // Reload RA bills for the selected contractor
    await loadEligibleRaBills({
      current: 1,
      pageSize: raBillsPagination.pageSize,
      sortOrder: "DESC",
      direction: "P",
      pageDiff: 0,
      paginationId: null,
      contractorIds: contractorId, // Pass contractor ID to filter
    });
  };

  const showRaBillSelection = () => {
    if (!selectedContractor) {
      Swal.fire("Warning", "Please select a contractor first", "warning");
      return;
    }
    // Initialize temp selection with currently selected bills
    setTempSelectedRaBills([...selectedRaBills]);
    setShowEligibleBills(true);
  };

  const handleTempRaBillSelection = (
    selectedRowKeys: React.Key[],
    selectedRows: ApprovedRaBillData[]
  ) => {
    setTempSelectedRaBills(selectedRows);
  };

  const handleRaBillSelection = (
    selectedRowKeys: React.Key[],
    selectedRows: ApprovedRaBillData[]
  ) => {
    setSelectedRaBills(selectedRows);

    // Create allocations for selected bills
    const allocations: any[] = selectedRows.map((bill: any, index) => ({
      id: `ALLOC${String(index + 1).padStart(3, "0")}`,
      raBillId: bill.raBillId,
      raBillNumber: bill.raBillSequenceId,
      workOrderId: bill.workOrderId,
      workOrderNumber: bill.workOrderNumber,
      raPeriod: bill.raPeriodFrom + " - " + bill.raPeriodTo,
      raBillValue: bill.grossTotal,
      alreadyPaid: bill.paidAmount,
      billBalance: bill.balanceAmount,
      thisPaymentAmount: bill.balanceAmount, // Default to full balance
      resultingStatus: "Paid" as const,
    }));

    form.setFieldsValue({ allocations });
    calculateTotals(allocations);
  };

  const confirmRaBillSelection = () => {
    handleRaBillSelection(
      tempSelectedRaBills.map((bill: any) => bill.raBillId),
      tempSelectedRaBills
    );
    setShowEligibleBills(false);
  };

  const cancelRaBillSelection = () => {
    setTempSelectedRaBills([...selectedRaBills]); // Reset temp selection
    setShowEligibleBills(false);
  };

  const calculateTotals = (allocations: PaymentAllocation[]) => {
    const sum = allocations.reduce(
      (total, allocation) => total + allocation.thisPaymentAmount,
      0
    );
    setSumOfAllocations(sum);

    const tdsRate = form.getFieldValue("tdsRate") || 1.0;
    const tds = (sum * tdsRate) / 100;
    setTdsThisPayment(tds);

    const net = sum - tds - otherAdjustments;
    setNetPayable(net);
  };

  const handleAllocationChange = (
    index: number,
    field: string,
    value: number
  ) => {
    const allocations = form.getFieldValue("allocations") || [];
    allocations[index] = { ...allocations[index], [field]: value };

    // Update resulting status
    if (field === "thisPaymentAmount") {
      const allocation = allocations[index];
      if (value >= allocation.billBalance) {
        allocation.resultingStatus = "Paid";
      } else if (value > 0) {
        allocation.resultingStatus = "Partially Paid";
      } else {
        allocation.resultingStatus = "Unpaid";
      }
    }

    form.setFieldsValue({ allocations });
    calculateTotals(allocations);
  };

  const removeAllocation = (index: number) => {
    const allocations = form.getFieldValue("allocations") || [];
    const updatedAllocations = allocations.filter(
      (_: any, i: number) => i !== index
    );
    form.setFieldsValue({ allocations: updatedAllocations });

    // Update selected RA bills
    const updatedSelectedBills = selectedRaBills.filter((_, i) => i !== index);
    setSelectedRaBills(updatedSelectedBills);

    calculateTotals(updatedAllocations);
  };

  const handleTdsRateChange = (value: number | null) => {
    if (value !== null) {
      const allocations = form.getFieldValue("allocations") || [];
      calculateTotals(allocations);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Prepare payment lines from allocations
      const lines = (values.allocations || []).map((allocation: any) => ({
        raBillId: allocation.raBillId,
        paidAmount: allocation.thisPaymentAmount,
        remarks: `Payment for RA Bill ${allocation.raBillNumber}`,
      }));

      // Create payment payload according to API format
      const paymentPayload = {
        contractorId: parseInt(values.contractorId),
        projectId: selectedRaBills[0]?.projectId || null, // Take from first selected bill
        // paymentReference: values.paymentVoucherNumber,
        // transactionNumber: values.paymentVoucherNumber,
        utrNumber: "", // Can be added later
        chequeNumber: "", // Can be added later
        paymentMethod: values.paymentMethod,
        paymentType: "RaBill",
        paymentDate: values.paymentDate?.format("YYYY-MM-DD"),
        bankAccountId: parseInt(values.bankAccountId),
        bankAccountNumber: "", // Can be fetched from bank account details
        bankName: "", // Can be fetched from bank account details
        totalPaymentAmount: sumOfAllocations,
        tdsSection: values.tdsSection || "",
        tdsRate: values.tdsRate || 0,
        otherDeductions: otherAdjustments,
        deductionReason: otherAdjustments > 0 ? "Other adjustments" : "",
        remarks: values.narration || "",
        narration: values.narration || "",
        currencyId: 1, // Default currency
        approvalRequired: false,
        isDraft: false,
        lines: lines,
        approvalHierarchy: [],
      };

      console.log("Payment payload:", paymentPayload);

      // Create FormData for multipart/form-data submission
      const formData = new FormData();

      // Append main payment data as JSON string
      formData.append('payment', JSON.stringify(paymentPayload));

      // Append files if any
      if (fileList && fileList.length > 0) {
        fileList.forEach((file, index) => {
          if (file.originFileObj) {
            formData.append(`attachments[${index}]`, file.originFileObj);
          }
        });
      }

      // Call the create payment API with FormData
      const apiResponse: any = await paymentApis.createPayment(formData);

      if (apiResponse.status) {
        Swal.fire("Success!", apiResponse.data.message, "success").then(
          () => {
            navigate(-1);
          }
        );
      } else {
        throw new Error(apiResponse.message || "Failed to create payment");
      }
    } catch (error: any) {
      console.error("Error creating payment:", error);
      Swal.fire(
        "Error!",
        error.message || "Failed to create payment. Please try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAsDraft = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Prepare payment lines from allocations
      const lines = (values.allocations || []).map((allocation: any) => ({
        raBillId: allocation.raBillId,
        paidAmount: allocation.thisPaymentAmount,
        remarks: `Payment for RA Bill ${allocation.raBillNumber}`,
      }));

      // Create payment payload for draft
      const paymentPayload = {
        contractorId: parseInt(values.contractorId),
        projectId: selectedRaBills[0]?.projectId || null,
        // paymentReference: values.paymentVoucherNumber,
        // transactionNumber: values.paymentVoucherNumber,
        utrNumber: "",
        chequeNumber: "",
        paymentMethod: values.paymentMethod,
        paymentType: "RaBill",
        paymentDate: values.paymentDate?.format("YYYY-MM-DD"),
        bankAccountId: parseInt(values.bankAccountId),
        bankAccountNumber: "",
        bankName: "",
        totalPaymentAmount: sumOfAllocations,
        tdsSection: values.tdsSection || "",
        tdsRate: values.tdsRate || 0,
        otherDeductions: otherAdjustments,
        deductionReason: otherAdjustments > 0 ? "Other adjustments" : "",
        remarks: values.narration || "",
        narration: values.narration || "",
        currencyId: 1,
        approvalRequired: false,
        isDraft: true, // Save as draft
        lines: lines,
        approvalHierarchy: [],
      };

      console.log("Draft payment payload:", paymentPayload);

      // Create FormData for multipart/form-data submission
      const formData = new FormData();

      // Append main payment data as JSON string
      formData.append('payment', JSON.stringify(paymentPayload));

      // Append files if any
      if (fileList && fileList.length > 0) {
        fileList.forEach((file, index) => {
          if (file.originFileObj) {
            formData.append(`attachments[${index}]`, file.originFileObj);
          }
        });
      }

      // Call the create payment API for draft with FormData
      const apiResponse: any = await paymentApis.createPayment(formData);

      if (apiResponse.status) {
        Swal.fire("Success!", "Payment saved as draft", "success").then(() => {
          navigate(-1);
        });
      } else {
        throw new Error(
          apiResponse.message || "Failed to save payment as draft"
        );
      }
    } catch (error: any) {
      console.error("Error saving draft:", error);
      Swal.fire(
        "Error!",
        error.message || "Failed to save payment as draft. Please try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // File upload handlers
  const handleFileUpload = async (file: any) => {
    setUploadLoading(true);
    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append("file", file);
      formData.append("documentType", "payment_attachment");
      formData.append("moduleId", "payments");

      // Here you would typically call a file upload API
      // const uploadResponse = await fileUploadApi.uploadFile(formData);

      // For now, simulate file upload success
      const uploadedFile = {
        uid: file.uid,
        name: file.name,
        status: "done",
        url: URL.createObjectURL(file), // Temporary URL for preview
        response: {
          fileId: `file_${Date.now()}`,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          uploadDate: new Date().toISOString(),
        },
      };

      setFileList((prev) => [...prev, uploadedFile]);
      return uploadedFile;
    } catch (error) {
      console.error("File upload failed:", error);
      throw error;
    } finally {
      setUploadLoading(false);
    }
  };

  const handleFileRemove = (file: any) => {
    setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
  };

  const beforeUpload = (file: any) => {
    const isValidType = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ].includes(file.type);
    if (!isValidType) {
      Swal.fire(
        "Error!",
        "You can only upload JPG/PNG/PDF/DOC/DOCX files!",
        "error"
      );
      return false;
    }
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      Swal.fire("Error!", "File must be smaller than 10MB!", "error");
      return false;
    }
    return true;
  };

  // RA Bills Selection Columns
  const raBillColumns: TableColumnsType<ApprovedRaBillData> = [
    {
      title: "RA Bill Number",
      dataIndex: "raBillSequenceId",
      key: "raBillSequenceId",
      width: 140,
    },
    {
      title: "Work Order",
      dataIndex: "workOrderNumber",
      key: "workOrderNumber",
      width: 140,
    },
    {
      title: "Site/Project",
      key: "siteProject",
      width: 200,
      render: (record: ApprovedRaBillData) => (
        <div>
          <div className="fw-bold">{record.siteName}</div>
          {record.projectName && (
            <small className="text-muted">{record.projectName}</small>
          )}
        </div>
      ),
    },
    {
      title: "RA Period",
      dataIndex: "raPeriod",
      key: "raPeriod",
      width: 200,
      render: (raPeriod, record: any) =>
        `${record.raPeriodFrom} to ${record.raPeriodTo}`,
    },
    {
      title: "Balance Amount",
      dataIndex: "balanceAmount",
      key: "balanceAmount",
      width: 120,
      render: (amount: number) => `₹${amount?.toLocaleString()}`,
    },
    {
      title: "Status",
      key: "status",
      width: 100,
      render: (record: ApprovedRaBillData) => (
        <div>
          <Tag color={record.status === "Approved" ? "green" : "blue"}>
            {record.status}
          </Tag>
          {record.hasSoftHold && <Tag color="orange">Soft Hold</Tag>}
        </div>
      ),
    },
  ];

  // Payment Allocations Columns
  const allocationColumns: TableColumnsType<any> = [
    {
      title: "#",
      key: "#",
      width: 50,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: "RA Bill Number",
      dataIndex: "raBillNumber",
      key: "raBillNumber",
      width: 140,
    },
    {
      title: "Work Order",
      dataIndex: "workOrderNumber",
      key: "workOrderNumber",
      width: 140,
    },
    {
      title: "RA Period",
      dataIndex: "raPeriod",
      key: "raPeriod",
      width: 100,
    },
    {
      title: "Bill Value",
      dataIndex: "raBillValue",
      key: "raBillValue",
      width: 120,
      render: (amount: number) => `₹${amount?.toLocaleString()}`,
    },
    {
      title: "Already Paid",
      dataIndex: "alreadyPaid",
      key: "alreadyPaid",
      width: 120,
      render: (amount: number) => `₹${amount?.toLocaleString()}`,
    },
    {
      title: "Balance",
      dataIndex: "billBalance",
      key: "billBalance",
      width: 120,
      render: (amount: number) => `₹${amount?.toLocaleString()}`,
    },
    {
      title: "This Payment",
      dataIndex: "thisPaymentAmount",
      key: "thisPaymentAmount",
      width: 140,
      render: (amount: number, _: any, index: number) => (
        <InputNumber
          value={amount}
          min={0}
          max={form.getFieldValue("allocations")?.[index]?.billBalance || 0}
          formatter={(value) =>
            `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
          }
          parser={(value) => value!.replace(/₹\s?|(,*)/g, "") as any}
          onChange={(value) =>
            handleAllocationChange(index, "thisPaymentAmount", value || 0)
          }
          style={{ width: "100%" }}
        />
      ),
    },
    // {
    //   title: "Resulting Status",
    //   dataIndex: "resultingStatus",
    //   key: "resultingStatus",
    //   width: 120,
    //   render: (status: string) => {
    //     const color =
    //       status === "Paid"
    //         ? "green"
    //         : status === "Partially Paid"
    //         ? "orange"
    //         : "red";
    //     return <Tag color={color}>{status}</Tag>;
    //   },
    // },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_: any, __: any, index: number) => (
        <AntButton
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeAllocation(index)}
        />
      ),
    },
  ];

  return (
    <>
      <Head title="Create Payment" />
      <Content>
        <Card className="card-bordered">
          <div className="card-inner card-inner-lg">
            <BlockHead size="lg">
              <BlockBetween>
                <BlockHeadContent>
                  <BlockTitle tag="h4">
                    {isEdit
                      ? "Edit Payment"
                      : isDuplicate
                      ? "Duplicate Payment"
                      : "Create Payment"}
                  </BlockTitle>
                </BlockHeadContent>
                <div className="d-flex">
                  <div className="actions clearfix">
                    <ul
                      className="m-auto d-flex align-items-center justify-content-center p-0"
                      style={{ flexDirection: "row-reverse" }}
                    >
                      <li className="pt-0 pb-0">
                        <Button
                          className="toggle"
                          color="primary"
                          onClick={() => navigate(-1)}
                        >
                          <Icon name="arrow-left" />
                          <span>Back</span>
                        </Button>
                      </li>
                    </ul>
                  </div>
                </div>
              </BlockBetween>
            </BlockHead>

            <div className="content clearfix">
              <Form form={form} layout="vertical" size="large">
                {/* Basic Information */}
                <Block>
                  {/* <h5>Basic Information</h5> */}
                  <Row gutter={[16, 16]}>
                    {/* <Col span={8}>
                      <Form.Item
                        name="paymentVoucherNumber"
                        label="Payment Voucher Number"
                        rules={[
                          {
                            required: true,
                            message: "Payment voucher number is required",
                          },
                        ]}
                      >
                        <Input disabled />
                      </Form.Item>
                    </Col> */}
                    <Col span={8}>
                      <Form.Item
                        name="paymentDate"
                        label="Payment Date"
                        rules={[
                          {
                            required: true,
                            message: "Payment date is required",
                          },
                        ]}
                      >
                        <DatePicker style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        name="paymentType"
                        label="Payment Type"
                        rules={[
                          {
                            required: true,
                            message: "Payment type is required",
                          },
                        ]}
                      >
                        <Select>
                          <Option value={PaymentType.RA}>RA Payment</Option>
                          <Option value={PaymentType.RetentionRelease}>
                            Release Retaintion Amount
                          </Option>
                          <Option value={PaymentType.OnHold}>
                            Release OnHold Amount
                          </Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <Form.Item
                        name="contractorId"
                        label="Contractor"
                        rules={[
                          { required: true, message: "Contractor is required" },
                        ]}
                      >
                        <Select
                          placeholder="Select contractor"
                          showSearch
                          loading={contractorLoading}
                          optionFilterProp="children"
                          onChange={handleContractorChange}
                        >
                          {contractorOptions.map((option) => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="legalEntityId"
                        label="Legal Entity"
                        rules={[
                          {
                            required: true,
                            message: "Legal entity is required",
                          },
                        ]}
                      >
                        <Select placeholder="Select legal entity">
                          {mockLegalEntityOptions.map((option) => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]}>
                    <Col span={8}>
                      <Form.Item
                        name="paymentMethod"
                        label="Payment Method"
                        rules={[
                          {
                            required: true,
                            message: "Payment method is required",
                          },
                        ]}
                      >
                        <Select placeholder="Select payment method">
                          {mockPaymentMethodOptions.map((option) => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        name="bankAccountId"
                        label="Bank Account"
                        rules={[
                          {
                            required: true,
                            message: "Bank account is required",
                          },
                        ]}
                      >
                        <Select placeholder="Select bank account">
                          {mockBankAccountOptions.map((option) => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    {/* <Col span={8}>
                      <Form.Item
                        name="tdsSection"
                        label="TDS Section"
                        rules={[{ required: true, message: "TDS section is required" }]}
                      >
                        <Select placeholder="Select TDS section">
                          {mockTdsSectionOptions.map(option => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col> */}
                  </Row>

                  <Row gutter={[16, 16]}>
                    {/* <Col span={6}>
                      <Form.Item
                        name="tdsRate"
                        label="TDS Rate (%)"
                        rules={[{ required: true, message: "TDS rate is required" }]}
                      >
                        <InputNumber
                          min={0}
                          max={100}
                          step={0.1}
                          style={{ width: "100%" }}
                          onChange={handleTdsRateChange}
                        />
                      </Form.Item>
                    </Col> */}
                    <Col span={18}>
                      <Form.Item name="narration" label="Narration">
                        <TextArea
                          rows={2}
                          placeholder="Enter payment narration"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Block>

                <Divider />

                {/* RA Bill Selection */}
                <Block>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5>Payment Allocations</h5>
                    <AntButton
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={showRaBillSelection}
                      disabled={!selectedContractor}
                    >
                      Add RA Bills
                    </AntButton>
                  </div>

                  <Form.Item name="allocations">
                    {selectedRaBills.length === 0 ? (
                      <div className="text-center py-4">
                        <Empty description="No RA bills selected. Click 'Add RA Bills' to select bills for payment." />
                      </div>
                    ) : (
                      <Table
                        columns={allocationColumns}
                        dataSource={form.getFieldValue("allocations") || []}
                        rowKey="id"
                        scroll={{ x: 1200 }}
                        bordered
                        size="small"
                        pagination={false}
                      />
                    )}
                  </Form.Item>
                </Block>

                <Divider />

                {/* Commercial Summary */}
                <Block>
                  <h5> Commercial Summary</h5>
                  <Row>
                    <Col span={12}>
                      <Descriptions bordered size="small" column={1}>
                        <Descriptions.Item label="Sum of Allocations">
                          <strong>₹{sumOfAllocations.toLocaleString()}</strong>
                        </Descriptions.Item>
                        <Descriptions.Item label="TDS This Payment">
                          <strong className="text-warning">
                            -₹{tdsThisPayment.toLocaleString()}
                          </strong>
                        </Descriptions.Item>
                        <Descriptions.Item label="Other Adjustments">
                          <strong>₹{otherAdjustments.toLocaleString()}</strong>
                        </Descriptions.Item>
                        <Descriptions.Item label="Net Payable">
                          <strong className="text-success">
                            ₹{netPayable.toLocaleString()}
                          </strong>
                        </Descriptions.Item>
                      </Descriptions>
                    </Col>
                  </Row>
                </Block>

                <Divider />

                {/* File Upload Section */}
                <Block>
                  <h5>Supporting Documents</h5>
                  <Row>
                    <Col span={24}>
                      <Form.Item
                        name="attachments"
                        label="Upload Documents"
                        help="Supported formats: JPG, PNG, PDF, DOC, DOCX (Max size: 10MB each)"
                      >
                        <Upload
                          name="file"
                          listType="text"
                          fileList={fileList}
                          beforeUpload={beforeUpload}
                          customRequest={async ({
                            file,
                            onSuccess,
                            onError,
                          }) => {
                            try {
                              const uploadedFile = await handleFileUpload(file);
                              onSuccess && onSuccess(uploadedFile);
                            } catch (error) {
                              onError && onError(error as any);
                            }
                          }}
                          onRemove={handleFileRemove}
                          multiple
                        >
                          <AntButton
                            icon={<UploadOutlined />}
                            loading={uploadLoading}
                            disabled={uploadLoading}
                          >
                            {uploadLoading ? "Uploading..." : "Upload Files"}
                          </AntButton>
                        </Upload>
                      </Form.Item>
                    </Col>
                  </Row>
                </Block>

                <Divider />

                {/* Action Buttons */}
                <div className="d-flex justify-content-end">
                  <Space>
                    <Button color="secondary" onClick={() => navigate(-1)}>
                      Cancel
                    </Button>
                    {/* <Button
                      color="default"
                      onClick={handleSaveAsDraft}
                      disabled={loading}
                    >
                      {loading ? <Spinner size="sm" /> : <SaveOutlined />}
                      Save as Draft
                    </Button> */}
                    <Button
                      color="primary"
                      onClick={handleSubmit}
                      disabled={loading || selectedRaBills.length === 0}
                    >
                      {loading ? (
                        <Spinner size="sm" />
                      ) : (
                        <FileTextOutlined classID="mr-2" />
                      )}
                      Submit
                    </Button>
                  </Space>
                </div>
              </Form>
            </div>
          </div>
        </Card>

        {/* RA Bills Selection Modal */}
        <Modal
          title={`Select RA Bills for Payment - ${
            contractorOptions.find((c) => c.value === selectedContractor)
              ?.label || ""
          }`}
          open={showEligibleBills}
          onCancel={cancelRaBillSelection}
          width={1200}
          footer={[
            <AntButton key="cancel" onClick={cancelRaBillSelection}>
              Cancel
            </AntButton>,
            <AntButton
              key="confirm"
              type="primary"
              onClick={confirmRaBillSelection}
              disabled={tempSelectedRaBills.length === 0}
            >
              Add Selected Bills ({tempSelectedRaBills.length})
            </AntButton>,
          ]}
        >
          {raBillsLoading ? (
            <>
              <Flex align="center" gap="middle" className="pageLoader">
                <Spin size="large" className="m-auto" />
              </Flex>
            </>
          ) : (
            <div className="table-responsive poTable">
              <div className="mb-3">
                <AntAlert
                  message={
                    tempSelectedRaBills.length > 0
                      ? `${tempSelectedRaBills.length} RA Bill(s) selected for payment`
                      : "Select RA Bills to create payment allocations"
                  }
                  type="info"
                  showIcon
                />
              </div>
              <div
                style={{
                  width: screenSize - 230 + "px",
                  margin: "0px auto 15px auto",
                }}
              >
                <Table
                  className="customTable"
                  columns={raBillColumns}
                  dataSource={eligibleRaBills}
                  rowKey="raBillId"
                  rowSelection={{
                    type: "checkbox",
                    selectedRowKeys: tempSelectedRaBills.map(
                      (bill: any) => bill.raBillId
                    ),
                    onSelect: (record, selected) => {
                      if (selected) {
                        handleTempRaBillSelection(
                          [
                            ...tempSelectedRaBills.map((b: any) => b.raBillId),
                            record.raBillId,
                          ],
                          [...tempSelectedRaBills, record]
                        );
                      } else {
                        const filtered = tempSelectedRaBills.filter(
                          (bill: any) => bill.raBillId !== record.raBillId
                        );
                        handleTempRaBillSelection(
                          filtered.map((b: any) => b.raBillId),
                          filtered
                        );
                      }
                    },
                    onSelectAll: (selected, selectedRows, changeRows) => {
                      if (selected) {
                        handleTempRaBillSelection(
                          eligibleRaBills.map((b) => b.raBillId),
                          eligibleRaBills
                        );
                      } else {
                        handleTempRaBillSelection([], []);
                      }
                    },
                  }}
                  scroll={{ x: 800 }}
                  bordered
                  size="small"
                  pagination={{
                    current: raBillsPagination.current,
                    pageSize: raBillsPagination.pageSize,
                    total: raBillsPagination.total,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "30", "40", "50"],
                    onChange: async (newCurrent, newPageSize) => {
                      const direction =
                        newCurrent > raBillsPagination.current ? "F" : "B";
                      const pageDiff =
                        Math.abs(newCurrent - raBillsPagination.current) - 1;

                      await loadEligibleRaBills({
                        current: newCurrent,
                        pageSize: newPageSize,
                        direction,
                        pageDiff,
                        sortOrder: "DESC",
                        paginationId:
                          direction === "F"
                            ? eligibleRaBills[eligibleRaBills.length - 1]
                                ?.paginationId
                            : eligibleRaBills[0]?.paginationId,
                      });
                    },
                  }}
                />
              </div>
            </div>
          )}
        </Modal>
      </Content>
    </>
  );
};

export default CreatePayment;